import { useState } from "react";
import { toast } from "sonner";
import { Play, Square, Lock } from "lucide-react";
import { startBot, stopBot, setMode, errText, usePoll, usd } from "@/lib/api";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

function Pill({ ok, label, testid }) {
  return (
    <span data-testid={testid} className={`px-2 py-0.5 rounded border text-[10px] uppercase tracking-widest ${ok ? "border-emerald-700 text-[var(--buy)]" : "border-[var(--border-active)] text-[var(--text-mute)]"}`}>
      {label}
    </span>
  );
}

function Balances({ data }) {
  const b = data?.balances || {};
  const cell = (env, label, testid) => (
    <div className={`px-3 py-1 ${data?.env === env ? "text-[var(--text)]" : "text-[var(--text-mute)]"}`}>
      <div className="text-[9px] uppercase tracking-widest">{label}</div>
      <div data-testid={testid} className="font-mono-num text-sm">{env in b ? usd(b[env]) : "—"}</div>
    </div>
  );
  return (
    <div data-testid="balances" className="flex rounded border border-[var(--border-active)] divide-x divide-[var(--border-subtle)]">
      {cell("demo", "Demo balance", "balance-demo")}
      {cell("live", "Real balance", "balance-real")}
    </div>
  );
}

export default function ControlBar({ s }) {
  const [busy, setBusy] = useState(false);
  const [confirmReal, setConfirmReal] = useState(false);
  const { data: bal } = usePoll("/balance", 15000);
  const realBal = bal?.balances?.live;
  const cover = (amt) => (realBal == null || !amt ? 0 : Math.floor((realBal + 1e-9) / amt));
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const real = s.env === "live";
  const askReal = () => {
    if (real) return;
    if (!s.live_available) return toast.error("Real money is locked: add Kalshi live API keys first");
    if (s.running || s.open_trades.length) return toast.error("Stop Estuary and close open trades before switching to real money");
    setConfirmReal(true);
  };
  return (
    <header data-testid="control-bar" className="flex flex-wrap items-center justify-between gap-4 py-5">
      <div>
        <h1 className="font-head text-4xl sm:text-5xl font-bold tracking-tight">Estuary</h1>
        <p className="text-sm text-[var(--text-dim)] mt-1">Late-window speed sniper · Kalshi 15-min BTC · ETH · SOL</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Pill testid="status-running" ok={s.running} label={s.running ? "Running" : "Stopped"} />
        <Pill testid="status-schedule" ok={s.schedule_open} label={s.schedule_open ? "Schedule open" : "Schedule closed"} />
        <Pill testid="status-window" ok={s.in_scan_window} label={s.in_scan_window ? "Scanning" : "Outside scan window"} />
        {s.halted && <Pill testid="status-halted" ok={false} label="Daily loss cap hit" />}
        <Balances data={bal} />
        <div data-testid="mode-toggle" className="flex rounded border border-[var(--border-active)] overflow-hidden text-xs">
          <button data-testid="mode-demo-btn" className={`px-3 py-1.5 ${!real ? "bg-cyan-500/20 text-cyan-100" : "text-[var(--text-dim)]"}`} onClick={() => act(() => setMode("demo"), "Demo mode")}>Demo</button>
          <button data-testid="mode-real-btn" title={s.live_available ? "Switch to real money" : "Add Kalshi live API keys to unlock"} className={`px-3 py-1.5 flex items-center gap-1 ${real ? "bg-amber-500/20 text-amber-100" : "text-[var(--text-mute)]"}`}
            onClick={askReal}>{!real && !s.live_available && <Lock className="w-3 h-3" />}Real</button>
        </div>
        <button data-testid="start-btn" disabled={busy || s.running || !s.credentials} onClick={() => act(startBot, "Estuary started")}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--buy)] text-black font-semibold text-sm disabled:bg-slate-700 disabled:text-slate-400 hover:brightness-110 transition-[filter]">
          <Play className="w-4 h-4" />Start
        </button>
        <button data-testid="stop-btn" disabled={busy} onClick={() => act(stopBot, "Stopped: orders cancelled, open trades sold")}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-500 text-white font-semibold text-sm disabled:opacity-40 hover:brightness-110 transition-[filter]">
          <Square className="w-4 h-4" />Stop &amp; sell all
        </button>
      </div>
      <AlertDialog open={confirmReal} onOpenChange={setConfirmReal}>
        <AlertDialogContent data-testid="real-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Switch to real money?</AlertDialogTitle>
            <AlertDialogDescription>
              Estuary will place real Kalshi orders with your current settings: {usd(s.per_trade_usd)} per trade (up to {usd(s.cycle_total_usd)} per cycle) and a ${s.config.daily_loss_cap_usd} daily loss cap.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div data-testid="real-confirm-balance" className="rounded border border-amber-700/60 px-3 py-2 text-sm font-mono-num">
            <div>Real balance: <span className="text-amber-200">{realBal == null ? "unavailable right now" : usd(realBal)}</span></div>
            {realBal != null && (
              <div data-testid="real-confirm-coverage" className="text-xs text-[var(--text-dim)] mt-1">
                Covers about {cover(s.cycle_total_usd)} full {cover(s.cycle_total_usd) === 1 ? "cycle" : "cycles"} at {usd(s.cycle_total_usd)} · {cover(s.per_trade_usd)} {cover(s.per_trade_usd) === 1 ? "trade" : "trades"} at {usd(s.per_trade_usd)} each
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="real-confirm-cancel">Stay on demo</AlertDialogCancel>
            <AlertDialogAction data-testid="real-confirm-yes" className="bg-amber-500 text-black hover:bg-amber-400"
              onClick={() => act(() => setMode("real"), "Real money on")}>Yes, use real money</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  );
}
